import type { getClients } from "@api/getClients.js";
import type { authenticationModalComponentMap } from "@components/auth-modal/authenticationModalComponentMap.js";
import type { ComponentProps } from "svelte";
import type { AuthenticationModalKind } from "@core/schema/AuthenticationModalKind.js";
import type {
	Customer,
	CustomerCreateB2CInput,
	CustomerEditMeInput,
	CustomerSignInWithGoogleInput,
} from "@core/schema/Customer.js";
import type { Locale } from "@core/schema/Locale.js";
import { PricingLevel } from "@core/schema/PricingLevel.js";
import type { Me } from "@core/schema/public/Me.js";
import { getCustomerType } from "@core/schema/utils/getCustomerType.js";
import type { LoginResponse } from "@core/utils/aws/cognito/LoginResponse.js";
import type { StorageState } from "@core/utils/svelte/StorageState.svelte.js";
import type { GlobalSettings } from "../contexts/GlobalSettings.js";
import type { GlobalTemplateProps } from "../contexts/GlobalTemplateProps.js";
import type { AuthChannel } from "./AuthChannel.js";
import type { AuthClient } from "./AuthClient.svelte.js";

export const Auth = new (class Auth implements Omit<AuthClient, "anonymousId"> {
	private _auth = $state<AuthClient>();
	private loadPromise: Promise<AuthClient> | undefined;

	private _signedIn = $state(false);

	public constructor() {
		if (typeof window !== "undefined") {
			this.loadPromise = this.load();
		}
	}

	private async load(): Promise<AuthClient> {
		const { AuthClient: RealAuthClient } = await import("./AuthClient.svelte.js");
		this._auth = new RealAuthClient();
		return this._auth;
	}

	private async ensureLoaded(): Promise<AuthClient> {
		return this._auth ?? (this.loadPromise ??= this.load());
	}

	public readonly signedIn = $derived(this._auth ? this._auth.signedIn : this._signedIn);
	public readonly loggedCustomer = $derived<Customer | null>(this._auth ? this._auth.loggedCustomer : null);
	public readonly authenticationModalKind = $derived<AuthenticationModalKind | undefined>(
		this._auth ? this._auth.authenticationModalKind : undefined,
	);
	public readonly customerType = $derived(this._auth ? this._auth.customerType : getCustomerType(null));
	public readonly loaded = $derived(this._auth ? this._auth.loaded : false);
	public readonly props = $derived(this._auth ? this._auth.props : {});
	public readonly identity = $derived(this._auth ? this._auth.identity : "");

	// The following members exist solely to satisfy `implements AuthClient` - they mirror internal
	// implementation details of AuthClient that no consumer of this wrapper is expected to touch directly.
	public get customerId(): StorageState<string | null> {
		if (!this._auth) {
			throw new Error("Auth not loaded yet");
		}
		return this._auth.customerId;
	}

	public get savedCustomer(): StorageState<Customer | null> {
		if (!this._auth) {
			throw new Error("Auth not loaded yet");
		}
		return this._auth.savedCustomer;
	}

	public get cognitoOAuthState(): StorageState<string | null> {
		if (!this._auth) {
			throw new Error("Auth not loaded yet");
		}
		return this._auth.cognitoOAuthState;
	}

	public get currentModalKind(): AuthenticationModalKind | undefined {
		return this._auth?.currentModalKind;
	}

	public set currentModalKind(value: AuthenticationModalKind | undefined) {
		if (this._auth) {
			this._auth.currentModalKind = value;
		}
	}

	public get settings(): GlobalSettings | undefined {
		return this._auth?.settings;
	}

	public set settings(value: GlobalSettings | undefined) {
		if (this._auth) {
			this._auth.settings = value;
		}
	}

	public get globalTemplateProps(): GlobalTemplateProps | undefined {
		return this._auth?.globalTemplateProps;
	}

	public set globalTemplateProps(value: GlobalTemplateProps | undefined) {
		if (this._auth) {
			this._auth.globalTemplateProps = value;
		}
	}

	public async getClients(): Promise<ReturnType<typeof getClients>> {
		const auth = await this.ensureLoaded();
		return auth.getClients();
	}

	public async setToCustomer(customer: Customer | null): Promise<void> {
		const auth = await this.ensureLoaded();
		await auth.setToCustomer(customer);
	}

	public async setToMe(me: Me | null): Promise<void> {
		const auth = await this.ensureLoaded();
		await auth.setToMe(me);
	}

	public reloadAfterSignIn(): void {
		if (!this._auth) {
			throw new Error("Auth not loaded yet");
		}
		this._auth.reloadAfterSignIn();
	}

	public async init(globalSettings: GlobalSettings, globalTemplateProps: GlobalTemplateProps): Promise<void> {
		this._signedIn = globalTemplateProps.pricingLevel !== PricingLevel.B2c;
		const auth = await this.ensureLoaded();
		await auth.init(globalSettings, globalTemplateProps);
	}

	public async openAuthenticationModal<K extends AuthenticationModalKind>(
		kind: K,
		props: ComponentProps<(typeof authenticationModalComponentMap)[K]>,
	): Promise<void> {
		const auth = await this.ensureLoaded();
		await auth.openAuthenticationModal(kind, props);
	}

	public closeAuthenticationModal(): void {
		this._auth?.closeAuthenticationModal();
	}

	public async refresh(): Promise<void> {
		const auth = await this.ensureLoaded();
		await auth.refresh();
	}

	public async initiateGoogleSignIn(
		locale: Locale,
		popup: Window | null,
		broadcastChannel: AuthChannel,
	): Promise<void> {
		const auth = await this.ensureLoaded();
		await auth.initiateGoogleSignIn(locale, popup, broadcastChannel);
	}

	public async completeGoogleSignIn(
		input: Omit<CustomerSignInWithGoogleInput, "fullName" | "email">,
		broadcastChannel: AuthChannel,
		code: string,
		state: string,
		redirectUri: string,
	): Promise<void> {
		const auth = await this.ensureLoaded();
		await auth.completeGoogleSignIn(input, broadcastChannel, code, state, redirectUri);
	}

	public async signIn(email: string, password: string): Promise<LoginResponse> {
		const auth = await this.ensureLoaded();
		return auth.signIn(email, password);
	}

	public async signInWithNewPassword(email: string, password: string, newPassword: string): Promise<LoginResponse> {
		const auth = await this.ensureLoaded();
		return auth.signInWithNewPassword(email, password, newPassword);
	}

	public async signOut(): Promise<void> {
		const auth = await this.ensureLoaded();
		await auth.signOut();
	}

	public async editMe(input: Partial<CustomerEditMeInput>): Promise<void> {
		const auth = await this.ensureLoaded();
		await auth.editMe(input);
	}

	public async forgotPassword(locale: Locale, email: string): Promise<void> {
		const auth = await this.ensureLoaded();
		await auth.forgotPassword(locale, email);
	}

	public async changePassword(previousPassword: string, proposedPassword: string): Promise<LoginResponse> {
		const auth = await this.ensureLoaded();
		return auth.changePassword(previousPassword, proposedPassword);
	}

	public async confirmForgotPassword(
		locale: Locale,
		email: string,
		confirmationCode: string,
		newPassword: string,
	): Promise<LoginResponse> {
		const auth = await this.ensureLoaded();
		return auth.confirmForgotPassword(locale, email, confirmationCode, newPassword);
	}

	public async confirmSignUp(locale: Locale, email: string, confirmationCode: string): Promise<boolean> {
		const auth = await this.ensureLoaded();
		return auth.confirmSignUp(locale, email, confirmationCode);
	}

	public async resendConfirmationCode(locale: Locale, email: string): Promise<boolean> {
		const auth = await this.ensureLoaded();
		return auth.resendConfirmationCode(locale, email);
	}

	public async signUp(input: CustomerCreateB2CInput): Promise<void> {
		const auth = await this.ensureLoaded();
		await auth.signUp(input);
	}
})();
